// aifab - Plugins page
// Plugins, Pipeline Templates, Custom Scripts - in tabs
import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, StatusBadge, EmptyState, Modal, ModalBody, ModalFooter, Err, Alert } from '../components/common';
import { PLUGIN_TYPES, PLUGIN_COLORS, PLUGIN_DESCRIPTIONS } from '../constants/plugins';

export default function PluginsPage() {
  const [tab, setTab] = useState('plugins');
  return (
    <div className="page">
      <div className="page-hd">
        <div>
          <div className="page-sub">Extend the training pipeline with custom pre/post-processing, evaluation, and templates</div>
        </div>
      </div>
      <div className="tabs">
        {[['plugins','Plugins'],['templates','Pipeline Templates'],['scripts','Custom Scripts']].map(([k,l]) => (
          <div key={k} className={`tab${tab===k?' on':''}`} onClick={() => setTab(k)}
            {...(k === 'templates' ? { 'data-tour': 'plugins-template' } : {})}>{l}</div>
        ))}
      </div>
      {tab === 'plugins'   && <PluginsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'scripts'   && <ScriptsTab />}
    </div>
  );
}

/* ── Plugins Tab ───────────────────────────────────────────────────────────── */
function PluginsTab() {
  const { data, loading, reload } = useApi('/plugins');
  const [filter,  setFilter]  = useState('ALL');
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = (data||[]).filter(p => filter === 'ALL' || p.plugin_type === filter);

  return (
    <>
      <div className="flex-btw mb16">
        <div className="flex gap6">
          {['ALL', ...PLUGIN_TYPES].map(t => (
            <button key={t} className={`btn btn-sm ${filter===t?'btn-primary':'btn-ghost'}`}
              onClick={() => setFilter(t)}>{t}</button>
          ))}
        </div>
        <button className="btn btn-primary btn-sm" data-tour="plugins-upload" onClick={() => setShow(true)}>+ Register Plugin</button>
      </div>

      {loading ? <Spinner/> : filtered.length === 0 ? (
        <EmptyState icon="⬢" title={filter==='ALL'?'No plugins yet':'No plugins of this type'}
          subtitle="Register a plugin to extend training pipeline stages"
          action={<button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ Register Plugin</button>} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:12 }}>
          {filtered.map(p => (
            <div key={p.id} className="card">
              <div style={{ padding:'14px 16px' }}>
                <div className="flex-btw mb10">
                  <div className="flex gap6">
                    <span className={`badge ${PLUGIN_COLORS[p.plugin_type]||'b-gray'}`}>{p.plugin_type}</span>
                    {p.is_locked
                      ? <span className="badge b-gray" title="Used in a run - edits create a new version">🔒 locked</span>
                      : <span className="badge b-green" title="No runs yet - editable in-place">✎ editable</span>}
                  </div>
                  <StatusBadge code={p.validation_status} />
                </div>
                <div className="bold mb4" style={{ fontSize:13 }}>{p.name}</div>
                <div className="xs c-3 mb4">{p.use_case} · <span className="mono">v{p.version}</span>
                  {p.version_seq > 1 && <span className="xs c-am ml8">seq {p.version_seq}</span>}
                </div>
                {p.description && <div className="xs c-3 mb8" style={{ lineHeight:1.5 }}>{p.description}</div>}
                <div className="sep" style={{ margin:'10px 0' }}/>
                <div className="flex-btw">
                  <span className="mono xs c-3">{p.checksum?.slice(0,10)}…</span>
                  <button className="btn btn-ghost btn-xs" onClick={() => setEditing(p)}>
                    {p.is_locked ? '⊕ New Version' : '✎ Edit'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && <RegisterPluginModal onClose={() => setShow(false)} onDone={() => { reload(); setShow(false); }}/>}

      {editing && (
        <EditPluginModal
          plugin={editing}
          onClose={() => setEditing(null)}
          onDone={(result) => {
            reload();
            setEditing(null);
            if (result?.is_new_version) {
              alert(`New version created: v${result.version} (seq ${result.version_seq})`);
            }
          }}
        />
      )}
    </>
  );
}

/* ── Pipeline Templates Tab ────────────────────────────────────────────────── */
function TemplatesTab() {
  const { data: templates, loading, reload } = useApi('/pipeline-templates');
  const { data: plugins }                    = useApi('/plugins');
  const [show, setShow] = useState(false);
  const [editingTmpl, setEditingTmpl] = useState(null);

  return (
    <>
      <div className="flex-btw mb16">
        <span className="xs c-3">
          Templates bundle plugins into reusable, version-pinned pipelines. Once created, plugin assignments are immutable - create a new template version to update.
        </span>
        <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ New Template</button>
      </div>

      {loading ? <Spinner/> : (templates||[]).length === 0 ? (
        <EmptyState icon="⬡" title="No pipeline templates"
          subtitle="Bundle plugins into a named, version-pinned template"
          action={<button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ New Template</button>} />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {(templates||[]).map(t => (
            <div key={t.id} className="card">
              <div style={{ padding:'14px 18px' }}>
                <div className="flex-btw mb12">
                  <div>
                    <span className="bold" style={{ fontSize:14 }}>{t.template_name}</span>
                    <span className="badge b-gray ml8">v{t.version}</span>
                    <span className="badge b-or ml8">{t.use_case}</span>
                  </div>
                  <div className="flex gap8" style={{ alignItems:'center' }}>
                    <span className={`badge ${t.is_active ? 'b-green' : 'b-gray'}`}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={t.is_locked ? 'Create new version' : 'Edit template'}
                      onClick={e => { e.stopPropagation(); setEditingTmpl(t); }}>
                      {t.is_locked ? '⊕ New Version' : '✎ Edit'}
                    </button>
                    {!t.is_locked && t.is_active && (
                      <button className="btn btn-ghost btn-xs"
                        style={{ color:'var(--red)', opacity:.7 }}
                        title="Deactivate this pipeline template"
                        onClick={async e => {
                          e.stopPropagation();
                          if (!window.confirm(`Deactivate "${t.template_name}"?`)) return;
                          try {
                            await apiFetch(`/pipeline-templates/${t.id}`, { method:'DELETE' });
                            reload();
                          } catch(err) { alert(err.message); }
                        }}>✕</button>
                    )}
                  </div>
                </div>

                {/* Visual pipeline slots */}
                <div className="pipeline-slots">
                  {[
                    ['PREPROCESS',   t.preprocess_plugin_id,   t.preprocess_version],
                    ['TRAIN',        t.train_plugin_id,         t.train_version],
                    ['EVALUATE',     t.evaluate_plugin_id,      t.evaluate_version],
                    ['POSTPROCESS',  t.postprocess_plugin_id,   t.postprocess_version],
                  ].map(([type, pid, ver]) => {
                    const plugin = pid ? (plugins||[]).find(p => p.id === pid) : null;
                    return (
                      <div key={type} className={`pipeline-slot${plugin?' filled':''}`}>
                        <div className="slot-type">
                          <span className={`badge ${PLUGIN_COLORS[type]||'b-gray'}`}>{type}</span>
                        </div>
                        <div>
                          {plugin ? (
                            <div>
                              <div className="slot-name">{plugin.name}</div>
                              <div className="xs c-3">v{plugin.version} · {plugin.use_case}</div>
                            </div>
                          ) : (
                            <div className="slot-empty">No plugin assigned</div>
                          )}
                        </div>
                        {ver && <div className="mono xs c-3">pinned v{ver}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingTmpl && (
        <EditTemplateModal
          template={editingTmpl}
          plugins={plugins||[]}
          onClose={() => setEditingTmpl(null)}
          onDone={(result) => {
            reload();
            setEditingTmpl(null);
            if (result?.is_new_version) {
              alert(`New version created: ${result.template_name} v${result.version}`);
            }
          }}
        />
      )}

      {show && (
        <NewTemplateModal
          plugins={plugins||[]}
          onClose={() => setShow(false)}
          onDone={() => { reload(); setShow(false); }}
        />
      )}
    </>
  );
}

/* ── Custom Scripts Tab ────────────────────────────────────────────────────── */
function ScriptsTab() {
  const { data, loading, reload } = useApi('/custom-scripts');
  const [showUpload, setShowUpload] = useState(false);

  return (
    <>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}>
          + Upload Script
        </button>
      </div>
      {loading ? <Spinner/> : (data||[]).length === 0 ? (
        <EmptyState icon="◉" title="No custom scripts"
          subtitle="Upload custom training scripts for the CUSTOM model family. Script must accept --params, --data-path, --work-dir and write result.json."
          action={<button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}>Upload Script</button>} />
      ) : (
        <div className="card">
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Name</th><th>Checksum</th><th>Description</th><th>Created</th></tr></thead>
              <tbody>
                {(data||[]).map(s => (
                  <tr key={s.script_id}>
                    <td className="bold">{s.script_name}</td>
                    <td className="td-mono td-muted">{s.checksum}</td>
                    <td className="td-muted">{s.description || '-'}</td>
                    <td className="td-muted">{s.created_at?.slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showUpload && <UploadScriptModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); reload(); }} />}
    </>
  );
}

/* ── Upload Script Modal ────────────────────────────────────────────────────── */
function UploadScriptModal({ onClose, onDone }) {
  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [file,        setFile]        = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState(null);

  const SKELETON = `#!/usr/bin/env python3
import argparse, json, os

ap = argparse.ArgumentParser()
ap.add_argument('--params',    required=True)
ap.add_argument('--data-path', required=True)
ap.add_argument('--work-dir',  required=True)
args = ap.parse_args()

params   = json.loads(args.params)
work_dir = args.work_dir
os.makedirs(f'{work_dir}/artifacts', exist_ok=True)

# ── your training logic here ──────────────────────────────────────

result = {
    'schema_version': '1.0',
    'status':         'success',
    'metrics':        {'f1_score': 0.0, 'accuracy': 0.0},
    'model_path':     'artifacts/model.pkl',
    'artifact_dir':   f'{work_dir}/artifacts',
}
with open(f'{work_dir}/result.json', 'w') as f:
    json.dump(result, f)
`;

  const downloadSkeleton = () => {
    const blob = new Blob([SKELETON], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'custom_train.py'; a.click();
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    if (!name.trim()) { setErr('Script name is required'); return; }
    if (!file)        { setErr('Please select a Python script file'); return; }
    setSaving(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('script_name',  name.trim());
      fd.append('description',  description.trim());
      fd.append('file',         file);
      await apiFetch('/custom-scripts', { method:'POST', body:fd, isFormData:true });
      onDone();
    } catch(e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Upload Custom Script" onClose={onClose}>
      <ModalBody>
        {err && <Err msg={err} />}
        <div className="form-row">
          <label className="form-lbl">Script name *</label>
          <input className="form-inp" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. my-bert-classifier" autoFocus />
        </div>
        <div className="form-row">
          <label className="form-lbl">Description</label>
          <input className="form-inp" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="What does this script do?" />
        </div>
        <div className="form-row">
          <label className="form-lbl">Script file (.py) *</label>
          <input type="file" accept=".py" className="form-inp"
            onChange={e => setFile(e.target.files[0] || null)} />
          <div className="xs c-3 mt4">
            Must accept <code>--params</code>, <code>--data-path</code>, <code>--work-dir</code> and write <code>result.json</code>.{' '}
            <button className="btn-link" onClick={downloadSkeleton}>Download skeleton ↓</button>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Uploading…' : 'Upload Script'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Register Plugin Modal ─────────────────────────────────────────────────── */
function RegisterPluginModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name:'', version:'1.0.0', plugin_type:'PREPROCESS', use_case:'', description:'' });
  const [code, setCode] = useState(getBoilerplate('PREPROCESS'));
  const [saving, setSaving] = useState(false);
  const [err,    setErr]   = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    if (!form.name||!form.use_case) { setErr('Name and use case required'); return; }
    setSaving(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('name',        form.name);
      fd.append('version',     form.version);
      fd.append('plugin_type', form.plugin_type);
      fd.append('use_case',    form.use_case);
      if (form.description) fd.append('description', form.description);
      fd.append('script', code);
      await apiFetch('/plugins', { method:'POST', body:fd, isFormData:true });
      onDone();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Register Plugin" subtitle="Write or paste your plugin code below" onClose={onClose} wide>
      <ModalBody>
        <Err msg={err} />
        <div className="form-row form-row-2 mb16">
          <div className="form-grp">
            <label className="form-lbl">Name *</label>
            <input className="form-inp" value={form.name} onChange={e => set('name',e.target.value)}
              placeholder="e.g. ITSM Text Cleaner" />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Version</label>
            <input className="form-inp" value={form.version} onChange={e => set('version',e.target.value)}
              placeholder="1.0.0" />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Type *</label>
            <select className="form-inp" value={form.plugin_type}
              onChange={e => { set('plugin_type',e.target.value); setCode(getBoilerplate(e.target.value)); }}>
              {PLUGIN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-grp">
            <label className="form-lbl">Use Case *</label>
            <input className="form-inp" value={form.use_case} onChange={e => set('use_case',e.target.value)}
              placeholder="e.g. itsm, fraud, churn" />
          </div>
        </div>

        <div className="form-grp mb12">
          <label className="form-lbl">Description</label>
          <input className="form-inp" value={form.description} onChange={e => set('description',e.target.value)}
            placeholder="What does this plugin do?" />
        </div>

        <div className="form-grp">
          <div className="flex-btw mb6">
            <label className="form-lbl">Plugin Code</label>
            <span className="xs c-3">Contract: {PLUGIN_DESCRIPTIONS[form.plugin_type]}</span>
          </div>
          <textarea className="form-inp code-area" value={code}
            onChange={e => setCode(e.target.value)} style={{ minHeight:300 }} />
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12}/> : 'Register Plugin'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ── New Template Modal ────────────────────────────────────────────────────── */
function NewTemplateModal({ plugins, onClose, onDone }) {
  const [form, setForm] = useState({
    template_name:'', version:'1.0.0', use_case:'',
    preprocess_plugin_id:'', evaluate_plugin_id:'',
    postprocess_plugin_id:'', train_plugin_id:'',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]   = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const byType = (type) => plugins.filter(p => p.plugin_type === type && p.is_active);

  const save = async () => {
    if (!form.template_name||!form.use_case) { setErr('Name and use case required'); return; }
    setSaving(true); setErr(null);
    try {
      const body = { ...form };
      ['preprocess','evaluate','postprocess','train'].forEach(k => {
        if (!body[`${k}_plugin_id`]) delete body[`${k}_plugin_id`];
      });
      await apiFetch('/pipeline-templates', { method:'POST', body:JSON.stringify(body) });
      onDone();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Pipeline Template" subtitle="Assign plugins to pipeline stages. Assignments are pinned - immutable after creation." onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <div className="form-row form-row-2 mb16">
          <div className="form-grp">
            <label className="form-lbl">Template Name *</label>
            <input className="form-inp" value={form.template_name} onChange={e => set('template_name',e.target.value)}
              placeholder="e.g. ITSM Standard Pipeline" />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Use Case *</label>
            <input className="form-inp" value={form.use_case} onChange={e => set('use_case',e.target.value)}
              placeholder="e.g. itsm, fraud, churn" />
          </div>
        </div>

        {/* Visual pipeline builder */}
        <div className="xs c-3 mb10" style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>
          Pipeline Stages
        </div>
        <div className="pipeline-slots">
          {[
            ['PREPROCESS',  'preprocess_plugin_id',  'Runs before training - data cleaning, encoding, feature transforms'],
            ['TRAIN',       'train_plugin_id',        'Override default training - full custom training loop'],
            ['EVALUATE',    'evaluate_plugin_id',     'Custom metrics - runs after training on test set'],
            ['POSTPROCESS', 'postprocess_plugin_id',  'Post-training transforms - calibration, threshold tuning'],
          ].map(([type, field, hint]) => {
            const opts = byType(type);
            const selected = form[field] ? opts.find(p=>p.id===form[field]) : null;
            return (
              <div key={type} className={`pipeline-slot${selected?' filled':''}`}>
                <div className="slot-type">
                  <span className={`badge ${PLUGIN_COLORS[type]||'b-gray'}`}>{type}</span>
                </div>
                <div style={{ flex:1 }}>
                  <select className="form-inp" style={{ background:'#E4EAF4', border:'none', padding:'4px 6px', fontSize:12, color:'#0F172A', borderRadius:4 }}
                    value={form[field]} onChange={e => set(field,e.target.value)}>
                    <option value="">- Optional -</option>
                    {opts.map(p => <option key={p.id} value={p.id}>{p.name} v{p.version}</option>)}
                  </select>
                  <div className="xs c-3">{hint}</div>
                </div>
                {selected && <span className="badge b-green xs">pinned</span>}
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12}/> : 'Create Template'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function getBoilerplate(type) {
  const templates = {
    PREPROCESS: `# PREPROCESS plugin
# Contract: transform(df: pd.DataFrame, config: dict) -> pd.DataFrame
import pandas as pd

def transform(df: pd.DataFrame, config: dict) -> pd.DataFrame:
    """
    Clean and prepare data before training.
    - df: raw training dataframe
    - config: feature config (target_column, feature_columns, etc.)
    Returns: transformed dataframe
    """
    # Example: drop nulls, strip text columns
    text_cols = df.select_dtypes(include='object').columns
    for col in text_cols:
        df[col] = df[col].fillna('').str.strip().str.lower()
    return df
`,
    EVALUATE: `# EVALUATE plugin
# Contract: evaluate(model, X_test, y_test: pd.Series, config: dict) -> dict
def evaluate(model, X_test, y_test, config: dict) -> dict:
    """
    Custom evaluation. Return a dict of metric_name -> float.
    """
    from sklearn.metrics import f1_score, accuracy_score
    y_pred = model.predict(X_test)
    return {
        'f1_weighted': f1_score(y_test, y_pred, average='weighted'),
        'accuracy': accuracy_score(y_test, y_pred),
    }
`,
    POSTPROCESS: `# POSTPROCESS plugin
# Contract: postprocess(result: dict, config: dict) -> dict
def postprocess(result: dict, config: dict) -> dict:
    """
    Modify inference result after prediction.
    - result: {'prediction': ..., 'probability': ..., 'label': ...}
    Returns: modified result dict
    """
    return result
`,
    TRAIN: `# TRAIN plugin (full override)
# Contract: train(df, config, params, work_dir) -> None
# Must write result.json to work_dir
import json, os

def train(df, config: dict, params: dict, work_dir: str) -> None:
    # Custom training logic
    # Write results
    with open(os.path.join(work_dir, 'result.json'), 'w') as f:
        json.dump({'f1_score': 0.0, 'accuracy': 0.0}, f)
`,
  };
  return templates[type] || '# Plugin code here\n';
}


/* ── Edit Plugin Modal ─────────────────────────────────────────────────────── */
function EditPluginModal({ plugin, onClose, onDone }) {
  const [form,    setForm]    = useState({
    name:           plugin.name,
    description:    plugin.description || '',
    script_content: plugin.script_content || '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const isLocked = plugin.is_locked;

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const result = await apiFetch(`/plugins/${plugin.id}`, {
        method: 'PATCH',
        body:   JSON.stringify(form),
      });
      onDone(result);
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={isLocked ? `New Version - ${plugin.name}` : `Edit Plugin - ${plugin.name}`}
      subtitle={isLocked
        ? `This plugin was used in a run. Your changes will create v${plugin.version.replace(/\.\d+$/, v => '.' + (parseInt(v.slice(1))+1))} automatically.`
        : 'Editing in-place - not yet used in any run.'}
      onClose={onClose} wide
    >
      <ModalBody>
        {isLocked && (
          <div className="alert alert-warn mb16">
            🔒 This plugin is locked - it was used in at least one run.
            Saving will create a new version so existing runs remain reproducible.
          </div>
        )}
        <Err msg={err} />
        <div className="form-row form-row-2 mb16">
          <div className="form-grp">
            <label className="form-lbl">Name</label>
            <input className="form-inp" value={form.name} onChange={e => set('name',e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Current Version</label>
            <input className="form-inp" value={plugin.version} disabled />
          </div>
        </div>
        <div className="form-grp mb16">
          <label className="form-lbl">Description</label>
          <input className="form-inp" value={form.description} onChange={e => set('description',e.target.value)} />
        </div>
        <div className="form-grp">
          <label className="form-lbl">Plugin Code</label>
          <textarea className="form-inp code-area" value={form.script_content}
            onChange={e => set('script_content',e.target.value)} style={{ minHeight:320 }} />
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12}/> : isLocked ? '⊕ Create New Version' : '✓ Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Edit Template Modal ───────────────────────────────────────────────────── */
function EditTemplateModal({ template, plugins, onClose, onDone }) {
  const [form, setForm] = useState({
    template_name:         template.template_name,
    description:           template.description || '',
    preprocess_plugin_id:  template.preprocess_plugin_id  || '',
    evaluate_plugin_id:    template.evaluate_plugin_id    || '',
    postprocess_plugin_id: template.postprocess_plugin_id || '',
    train_plugin_id:       template.train_plugin_id       || '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const isLocked = template.is_locked;
  const byType = (type) => plugins.filter(p => p.plugin_type === type && p.is_active);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      // Send null for cleared slots, omit unchanged
      const body = { ...form };
      ['preprocess','evaluate','postprocess','train'].forEach(k => {
        if (body[`${k}_plugin_id`] === template[`${k}_plugin_id`]) {
          delete body[`${k}_plugin_id`];
        } else if (!body[`${k}_plugin_id`]) {
          body[`${k}_plugin_id`] = ''; // signal to clear
        }
      });
      const result = await apiFetch(`/pipeline-templates/${template.id}`, {
        method: 'PATCH',
        body:   JSON.stringify(body),
      });
      onDone(result);
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={isLocked ? `New Template Version - ${template.template_name}` : `Edit Template - ${template.template_name}`}
      subtitle={isLocked
        ? 'Used in a run. Changes will create a new version automatically.'
        : 'Editing in-place - not yet used in any run.'}
      onClose={onClose}
    >
      <ModalBody>
        {isLocked && (
          <div className="alert alert-warn mb16">
            🔒 This template is locked. Saving creates a new version - existing runs remain unaffected.
          </div>
        )}
        <Err msg={err} />
        <div className="form-row form-row-2 mb16">
          <div className="form-grp">
            <label className="form-lbl">Template Name</label>
            <input className="form-inp" value={form.template_name}
              onChange={e => set('template_name',e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Current Version</label>
            <input className="form-inp" value={template.version} disabled />
          </div>
        </div>
        <div className="xs c-3 mb10" style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>
          Pipeline Stages
        </div>
        <div className="pipeline-slots">
          {[
            ['PREPROCESS',  'preprocess_plugin_id',  'Runs before training'],
            ['TRAIN',       'train_plugin_id',        'Custom training override'],
            ['EVALUATE',    'evaluate_plugin_id',     'Custom metrics'],
            ['POSTPROCESS', 'postprocess_plugin_id',  'Post-training transforms'],
          ].map(([type, field, hint]) => {
            const opts     = byType(type);
            const selected = form[field] ? opts.find(p => p.id === form[field]) : null;
            return (
              <div key={type} className={`pipeline-slot${selected?' filled':''}`}>
                <div className="slot-type">
                  <span className={`badge ${PLUGIN_COLORS[type]||'b-gray'}`}>{type}</span>
                </div>
                <div style={{ flex:1 }}>
                  <select className="form-inp"
                    style={{ background:'#E4EAF4', border:'none', padding:'4px 6px', fontSize:12, color:'#0F172A', borderRadius:4 }}
                    value={form[field]}
                    onChange={e => set(field,e.target.value)}>
                    <option value="">- None -</option>
                    {opts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} v{p.version}{p.is_locked ? ' 🔒' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="xs c-3">{hint}</div>
                </div>
                {selected && <span className="badge b-green xs">assigned</span>}
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12}/> : isLocked ? '⊕ Create New Version' : '✓ Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
}
